/**
 * @file Live tuning layer over weapon archetypes.
 *
 * `WEAPONS` stays the shipped balance table; this is the editable copy on top of
 * it. The mechanism — sparse overrides per `weaponKey.path`, persistence, the
 * merged read-only view, the debug-panel rows — is
 * {@link ./keyedTuning.js createKeyedTuningLayer}, shared with the enemy layer.
 * What lives here is what is actually weapon-specific: the editing rules, which
 * leaves are off limits, and which weapons the panel lists.
 */

import { WEAPONS } from './Weapons.js';
import { createKeyedTuningLayer } from './keyedTuning.js';

export const WEAPON_TUNING_STORAGE_KEY = 'spin.weaponTuning.v1';

/**
 * Per-field editing rules. Missing entries still render with a human label and
 * conservative numeric defaults, so new weapon fields show up automatically.
 * @type {Readonly<Record<string, { label: string, step: number, min: number, integer?: boolean }>>}
 */
const WEAPON_META = Object.freeze({
  modelScale: { label: 'Held model scale', step: 0.05, min: 0.01 },
  damage: { label: 'Damage', step: 1, min: 0 },
  fireRate: { label: 'Fire rate', step: 0.05, min: 0.01 },
  burstCount: { label: 'Burst rounds', step: 1, min: 1, integer: true },
  burstInterval: { label: 'Burst interval', step: 0.01, min: 0.01 },
  projectileSpeed: { label: 'Projectile speed', step: 1, min: 0.01 },
  range: { label: 'Range', step: 1, min: 0.01 },
  projectileRadius: { label: 'Projectile radius', step: 0.01, min: 0.01 },
  spread: { label: 'Spread', step: 0.001, min: 0 },
  pellets: { label: 'Pellets', step: 1, min: 1, integer: true },
  pierce: { label: 'Pierce', step: 1, min: 0, integer: true },
  muzzleForward: { label: 'Muzzle forward', step: 0.05, min: 0 },
  muzzleHeight: { label: 'Muzzle height', step: 0.05, min: 0 },
  shake: { label: 'Shake', step: 0.01, min: 0 },
  knockback: { label: 'Knockback ×', step: 0.05, min: 0 },
  maxDamagePerTarget: { label: 'Max damage/target', step: 1, min: 0 },
  falloffStart: { label: 'Falloff start', step: 0.01, min: 0 },
  falloffMin: { label: 'Falloff min', step: 0.01, min: 0 },
  muzzleParticles: { label: 'Muzzle particles', step: 1, min: 0, integer: true },
  magazineCapacity: { label: 'Magazine', step: 1, min: 1, integer: true },
  startingAmmo: { label: 'Starting ammo', step: 1, min: 0, integer: true },
  reloadDuration: { label: 'Reload duration', step: 0.05, min: 0 },
  projectileLifetime: { label: 'Projectile life', step: 0.05, min: 0 },
  burnDamagePerSecond: { label: 'Burn DPS', step: 0.5, min: 0 },
  burnDurationSeconds: { label: 'Burn duration', step: 0.05, min: 0 },
  spreadDegrees: { label: 'Spread degrees', step: 0.1, min: 0 },
  'explosive.radius': { label: 'Blast radius', step: 0.1, min: 0.01 },
  'explosive.damage': { label: 'Blast damage', step: 1, min: 0 },
  'explosive.selfDamageMultiplier': { label: 'Self damage mult', step: 0.01, min: 0 },
  'explosive.shake': { label: 'Blast shake', step: 0.01, min: 0 },
  'trail.distance': { label: 'Trail distance', step: 0.05, min: 0.01 },
  'trail.size': { label: 'Trail size', step: 0.01, min: 0.01 },
  'trail.life': { label: 'Trail life', step: 0.05, min: 0.01 },
  'laser.width': { label: 'Laser width', step: 0.005, min: 0.005 },
  'laser.opacity': { label: 'Laser opacity', step: 0.05, min: 0 },
  'projectile.radius': { label: 'Visual radius', step: 0.01, min: 0.01 },
  'projectile.length': { label: 'Visual length', step: 0.05, min: 0.01 },
  'projectile.flashIntensity': { label: 'Flash intensity', step: 0.5, min: 0 },
  'projectile.flashLife': { label: 'Flash life', step: 0.01, min: 0 }
});

const DEFAULT_META = Object.freeze({ label: '', step: 0.1, min: 0 });

/**
 * Packed colours, masks and layers are numbers that are not balance knobs: an
 * edited bitmask is a broken weapon, not a rebalanced one.
 * @param {string} path Full dotted path.
 * @param {string} key The leaf's own key.
 * @returns {boolean}
 */
function isSkippedNumeric(path, key) {
  return /color|mask|layer/i.test(path) || /color|mask|layer/i.test(key);
}

/**
 * Which weapons the debug panel offers. Enemy variants and the melee fallback
 * are playable-adjacent rather than playable, and the flamethrower is driven by
 * the stream system rather than the shot pipeline.
 *
 * @param {string} key Weapon key.
 * @param {import('./Weapons.js').WeaponArchetype} [archetype] Its row.
 * @returns {boolean}
 */
export function isInspectableWeapon(key, archetype = WEAPONS[key]) {
  return (
    Boolean(archetype) &&
    key !== 'flamethrower' &&
    !key.endsWith('_enemy') &&
    archetype.type !== 'melee'
  );
}

const layer = createKeyedTuningLayer({
  table: WEAPONS,
  storageKey: WEAPON_TUNING_STORAGE_KEY,
  meta: WEAPON_META,
  defaultMeta: DEFAULT_META,
  isSkipped: isSkippedNumeric,
  isInspectable: isInspectableWeapon,
  exportLabel: 'weapons'
});

export const setWeaponTuningStorage = layer.setStorage;
/** @returns {typeof WEAPONS} `WEAPONS` with the current overrides applied. */
export const getWeaponTuning = () => /** @type {typeof WEAPONS} */ (layer.getTuning());
export const getWeaponArchetype = layer.getEntry;
export const weaponTuningPaths = layer.tuningPaths;
export const getWeaponFieldMeta = layer.getFieldMeta;
export const getWeaponDefaultValue = layer.getDefaultValue;
export const setWeaponValue = layer.setValue;
export const getWeaponOverrides = layer.getOverrides;
export const isWeaponValueOverridden = layer.isValueOverridden;
export const applyWeaponOverrides = layer.applyOverrides;
export const saveWeaponTuning = layer.save;
export const loadWeaponTuning = layer.load;
export const resetWeaponTuning = layer.reset;
export const exportWeaponTuning = () => layer.export();
export const onWeaponTuningChanged = layer.onChanged;
export const inspectWeaponTuning = layer.inspect;
